import {
  ConnectionConfiguration,
  ConnectionConfigurationCreate,
  ConnectionCreate,
  Connection,
  MySQLConnectionConfigurationBE,
} from "src/api/connectApi/types";
import {
  filterSecretsByEnvironment,
  filterUpdatedSecrets,
  findSecret,
  getDefaultSecret,
  markSecrets,
  setSecretDeleted,
  setSecretEnv,
} from "src/connections/model/common";
import {
  KeyValuePair,
  MySQLConnectionConfigInputs,
  MySQLConnectionConfig,
} from "src/connections/types";

export const getMySQLConnectionConfigDefaultValues = (
  env: string | null,
): MySQLConnectionConfig => ({
  databaseName: "",
  host: "",
  port: null,
  allowTestInvocations: false,
  authMethod: "username_password",
  usernamePasswordConfig: {
    username: getDefaultSecret("username", env),
    password: getDefaultSecret("password", env),
  },
  hasSSHTunnelEnabled: false,
  SSHTunnel: {
    host: "",
    port: 22,
    user: "",
    privateKey: getDefaultSecret("ssh_private_key", env),
  },
});

export const getMySQLConnectionConfigInputsDefaultValues =
  (): MySQLConnectionConfigInputs => ({
    name: "",
    dataRetention: {
      value: 0,
      unit: "days",
    },
    enableNonProdConfigs: false,
    productionConfig: getMySQLConnectionConfigDefaultValues(null),
    sandboxConfig: getMySQLConnectionConfigDefaultValues("sandbox"),
  });

const convertBEConnectionConfigurationToMySQLConnectionConfig = (
  config: ConnectionConfiguration,
  envSecrets: KeyValuePair[],
  env: string | null,
): MySQLConnectionConfig => {
  const mysqlConfig = config as MySQLConnectionConfigurationBE;
  return {
    databaseName: mysqlConfig.database_name,
    host: mysqlConfig.host,
    port: mysqlConfig.port,
    allowTestInvocations: config.allow_test_invocations ?? false,
    authMethod: mysqlConfig.auth_method,
    usernamePasswordConfig: {
      username:
        findSecret(envSecrets, "username") ?? getDefaultSecret("username", env),
      password:
        findSecret(envSecrets, "password") ?? getDefaultSecret("password", env),
    },
    hasSSHTunnelEnabled: mysqlConfig.has_ssh_tunnel_enabled ?? false,
    SSHTunnel: {
      host: mysqlConfig.ssh_tunnel?.host ?? "",
      port: mysqlConfig.ssh_tunnel?.port ?? 22,
      user: mysqlConfig.ssh_tunnel?.user ?? "",
      privateKey:
        findSecret(envSecrets, "ssh_private_key") ??
        getDefaultSecret("ssh_private_key", env),
    },
  };
};

const convertBENonProdEnvConfigToMySQLConnectionConfigs = (
  nonProdEnvConfigs: Record<string, ConnectionConfiguration>,
  env: string,
  allSecrets: KeyValuePair[],
): MySQLConnectionConfig | null => {
  if (!nonProdEnvConfigs?.[env]) return null;
  const envSecrets = filterSecretsByEnvironment(allSecrets, env);
  return convertBEConnectionConfigurationToMySQLConnectionConfig(
    nonProdEnvConfigs[env],
    envSecrets,
    env,
  );
};

/**
 * Converts BE MySQL connection configuration to FE format.
 * If the provider of the connection is not "mysql", it throws an error.
 *
 * @param {Connection} connection - The MySQL connection object in BE format.
 * @returns {MySQLConnectionConfigInputs} - The converted MySQL connection object in FE format suitable to use with components.
 * @throws {Error} - Throws an error if the provider of the connection is not "mysql".
 */
export const convertBEConnectionToMySQLConnectionConfigInputs = (
  connection: Connection,
): MySQLConnectionConfigInputs => {
  if (connection.provider !== "mysql")
    throw new Error(
      `Invalid connection type: ${connection.provider} for MySQL converter`,
    );

  const defaultValues = getMySQLConnectionConfigInputsDefaultValues();
  const nonProdEnvConfigs = connection.non_prod_env_configs;

  const allSecrets = markSecrets(connection.secrets) ?? [];
  // Convert "null" to "production" value if necessary
  const prodSecrets = filterSecretsByEnvironment(allSecrets, null);
  return {
    ...defaultValues,
    name: connection.name,
    dataRetention: connection.data_retention ?? defaultValues.dataRetention,
    enableNonProdConfigs: connection.enable_non_prod_configs,
    productionConfig: convertBEConnectionConfigurationToMySQLConnectionConfig(
      connection.configuration,
      prodSecrets,
      null,
    ),
    sandboxConfig:
      nonProdEnvConfigs && nonProdEnvConfigs["sandbox"]
        ? convertBENonProdEnvConfigToMySQLConnectionConfigs(
            nonProdEnvConfigs,
            "sandbox",
            allSecrets,
          )
        : getMySQLConnectionConfigDefaultValues("sandbox"),
  };
};

const extractSecretsFromMySQLConnectionConfig = (
  config: MySQLConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.authMethod === "username_password") {
    return [
      setSecretEnv(config.usernamePasswordConfig.username, env),
      setSecretEnv(config.usernamePasswordConfig.password, env),
    ];
  }
  return [];
};

const extractSecretsFromMySQLConnectionSSHTunnelConfig = (
  config: MySQLConnectionConfig,
  env: string | null,
): KeyValuePair[] => {
  if (config.hasSSHTunnelEnabled && config.SSHTunnel) {
    return [setSecretEnv(config.SSHTunnel.privateKey, env)];
  }
  // If SSH tunnel is disabled, we need to delete the secrets that are present
  if (config.SSHTunnel) {
    return [config.SSHTunnel.privateKey]
      .filter((secret) => secret?.value !== "")
      .map((s) => setSecretEnv(s, env))
      .map(setSecretDeleted);
  }
  return [];
};

const extractSecretsFromMySQLConnectionConfigInputs = (
  config: MySQLConnectionConfigInputs,
): KeyValuePair[] => {
  const secrets = extractSecretsFromMySQLConnectionConfig(
    config.productionConfig,
    null,
  );
  secrets.push(
    ...extractSecretsFromMySQLConnectionSSHTunnelConfig(
      config.productionConfig,
      null,
    ),
  );

  // Only include non-prod secrets if non-prod configs are enabled
  if (config.enableNonProdConfigs && config.sandboxConfig) {
    secrets.push(
      ...extractSecretsFromMySQLConnectionConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
    secrets.push(
      ...extractSecretsFromMySQLConnectionSSHTunnelConfig(
        config.sandboxConfig,
        "sandbox",
      ),
    );
  }

  // Removing un-changed secrets, so that they are not sent to the backend
  return filterUpdatedSecrets(secrets);
};

const convertMySQLConnectionConfigToBEConnectionConfiguration = (
  config: MySQLConnectionConfig,
): ConnectionConfigurationCreate => {
  return {
    database_name: config.databaseName,
    host: config.host,
    port: config.port || null, // Replace "" with null when port is deleted
    auth_method: config.authMethod,
    allow_test_invocations: config.allowTestInvocations,
    has_ssh_tunnel_enabled: config.hasSSHTunnelEnabled,
    ssh_tunnel: config.hasSSHTunnelEnabled
      ? {
          host: config.SSHTunnel?.host ?? "",
          port: config.SSHTunnel?.port ?? 22,
          user: config.SSHTunnel?.user ?? "",
        }
      : undefined,
  };
};

/**
 * Utility to strip the 'required' property from secrets before sending to BE
 * Only keep 'required: true' for secrets where value is an empty string and required is true.
 * Only include 'tenant_id' if it was present in the original secret.
 */
const stripRequiredFromSecrets = (secrets: KeyValuePair[]): KeyValuePair[] =>
  secrets.map((secret) => {
    // Clone the secret so we do not mutate the original object
    const sanitized: KeyValuePair = { ...secret } as KeyValuePair;
    // Remove the `required` field unless the secret is required and value is empty string
    if (!(secret.required && secret.value === "")) {
      delete sanitized.required;
    }
    return sanitized;
  });

/**
 * Converts FE MySQL connection object to BE format.
 *
 * @param {MySQLConnectionConfigInputs} inputs - The MySQL connection object in FE format.
 * @returns {ConnectionCreate} - The MySQL connection object in BE format suitable for sending POST request.
 */
export const convertMySQLConnectionConfigInputsToBEConnection = (
  inputs: MySQLConnectionConfigInputs,
): ConnectionCreate => {
  // Strip 'required' from each secret before sending to BE
  const secrets = stripRequiredFromSecrets(
    extractSecretsFromMySQLConnectionConfigInputs(inputs),
  );

  return {
    name: inputs.name,
    is_sandbox: false,
    provider: "mysql",
    configuration: convertMySQLConnectionConfigToBEConnectionConfiguration(
      inputs.productionConfig,
    ),
    secrets,
    data_retention: inputs.dataRetention,
    enable_non_prod_configs: inputs.enableNonProdConfigs,
    non_prod_env_configs:
      inputs.enableNonProdConfigs && inputs.sandboxConfig
        ? {
            sandbox: convertMySQLConnectionConfigToBEConnectionConfiguration(
              inputs.sandboxConfig,
            ),
          }
        : {},
  };
};
